import * as React from 'react';

import Radium from '@flipdish/radium';
import Skeleton from 'react-loading-skeleton';
import ProgressiveImage from 'react-progressive-image';
import { connect } from 'react-redux';

import { Typography as ExtendTypography } from '../../../ui';
import { Track } from '../../analytics';
import * as Api from '../../Api';
import { Data, Item, MaximumPrice, ToCurrency } from '../../BasketControl';
import * as Store from '../../Store';
import { Utilities } from '../../Utilities';
import LazyLoad from '../LazyLoad';

const flatten = require('lodash/flatten');
const tinycolor = require('tinycolor2');
const uuidv4 = require('uuid/v4');

interface Props {
  MenuId: number;
  MenuSectionId: number;
  Basket: Data;
  RemoveItemAt: (payload: {
    menuId: number;
    index: number;
    indexName?: string;
  }) => void;
  AddItemToBasket: (payload: Item) => void;
  basketCount: number;
  Currency: Api.Currency;
  HandleClickItem: () => any;
  Item: Api.MenuItem;
  vrid: number;
  translate: any;
  Languages: Store.Language[];
  AppDesignScheme: Api.AppDesignScheme;
  isDark: boolean;
}

interface State {}

const Typography = ExtendTypography as any;

type ImageDimension = {
  width: number;
  height: number;
};

export const getImageDimensions = (
  appConfig: any,
  cellLayoutType:
    | Api.CellLayoutType
    | 'Small'
    | 'Medium'
    | 'Large'
    | 'HiddenImage',
): ImageDimension => {
  const showOldImages =
    appConfig.status === 'success'
      ? appConfig.data.Features.includes('keep-old-image-sizes')
      : false;

  const imageSizes = [
    // Indexed by cellLayoutType 0/1/2/3
    { width: 120, height: 120, className: '' },
    { width: 120, height: 120, className: '' },
    { width: 560, height: showOldImages ? 180 : 336, className: '' },
    { width: 560, height: showOldImages ? 180 : 336, className: '' },
  ];

  const { width, height } = imageSizes[cellLayoutType];
  return { width, height };
};

class FlipdishMenuItem extends React.Component<Props & MappedState, State> {
  private fromText = (): string => {
    const { ActualPrice } = this.props.Item;

    if (MaximumPrice(this.props.Item) > ActualPrice) {
      return `${this.props.translate('From')} `;
    }

    return '';
  };

  public render() {
    const {
      AddItemToBasket,
      AppConfig,
      AppDesignScheme,
      Basket,
      basketCount,
      isDark,
      Item,
      RemoveItemAt,
      translate,
    } = this.props;
    const {
      Name,
      Description,
      MenuItemOptionSets,
      CellLayoutType,
      MenuItemId,
    } = Item;

    const styles = {
      item: {
        borderColor: tinycolor(AppDesignScheme.Secondary)
          .setAlpha(0.4)
          .toString(),
        ':hover': {},
        ':active': {},
      },
      darkItem: {
        borderColor: AppDesignScheme.Primary,
        ':hover': {},
        ':active': {},
      },
      tertiary: {
        color: AppDesignScheme.Tertiary,
      },
      primary: {
        color: AppDesignScheme.Primary,
      },
      secondary: {
        color: AppDesignScheme.Secondary,
      },
      backgroundSecondary: {
        backgroundColor: AppDesignScheme.Secondary,
      },
      backgroundSecondaryOpacity: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .setAlpha(0.8)
          .toString(),
      },
      added: {
        backgroundColor: AppDesignScheme.Secondary,
        ':hover': {
          boxShadow: `-3px 6px 9px -5px ${AppDesignScheme.Secondary}`,
        },
        ':active': {
          boxShadow: `-3px 6px 9px -5px ${AppDesignScheme.Secondary}`,
        },
      },
      control: {
        color: AppDesignScheme.Primary,
        ':hover': {
          color: AppDesignScheme.Tertiary,
        },
        ':active': {
          color: tinycolor(AppDesignScheme.Tertiary)
            .darken(10)
            .toString(),
        },
      },
      controlTwo: {
        color: AppDesignScheme.Tertiary,
        ':hover': {
          color: AppDesignScheme.Secondary,
        },
        ':active': {
          color: tinycolor(AppDesignScheme.Secondary)
            .darken(10)
            .toString(),
        },
      },
      controlThree: {
        color: AppDesignScheme.Primary,
        ':hover': {
          color: AppDesignScheme.Secondary,
        },
        ':active': {
          color: tinycolor(AppDesignScheme.Secondary)
            .darken(10)
            .toString(),
        },
      },
      imgHover: {
        borderColor: AppDesignScheme.Secondary,
        boxShadow: `-3px 6px 9px -5px ${AppDesignScheme.Secondary}`,
      },
      borderSecondary: {
        borderColor: AppDesignScheme.Secondary,
      },
    };

    const optionSetItems = flatten(
      MenuItemOptionSets.map((os) => os.MenuItemOptionSetItems),
    );

    let descriptionWithLineBreaks;

    let initialLang = 'en';

    if (this.props.Languages) {
      for (const Language of this.props.Languages) {
        if (Language.active) {
          initialLang = Language.code;
          break;
        }
      }
    }

    if (Description) {
      descriptionWithLineBreaks = Utilities.stringToJSX(
        Description.split('\n'),
        (part: string) => [part, <br key={uuidv4()} />],
      );
      descriptionWithLineBreaks.pop();
    } else {
      descriptionWithLineBreaks = '';
    }

    const from = this.fromText();

    let prePrice = null;
    let postPrice = null;

    if (optionSetItems.length > 0) {
      postPrice = (
        <i
          className="secondary fa fa-caret-right align-self-center"
          aria-hidden="true"
          style={
            Radium.getState(this.state, `item-${MenuItemId}`, ':active') ||
            basketCount > 0
              ? styles.primary
              : styles.secondary
          }
        />
      );
    }

    if (from != '') {
      prePrice = (
        <p
          className="secondary clear-margins bold text-right fd-h5"
          style={
            Radium.getState(this.state, `item-${MenuItemId}`, ':active') ||
            basketCount > 0
              ? styles.primary
              : styles.secondary
          }
        >
          {from}
        </p>
      );
    }

    let img: JSX.Element | null = null;

    if (this.props.Item.ImageUrl) {
      const { width, height } = getImageDimensions(AppConfig, CellLayoutType);

      const url = `${this.props.Item.ImageUrl}?auto=format&w=${width}&h=${height}`;

      const placeholder =
        CellLayoutType ===
          ('Small' as Fd.MenuSectionItemBase['CellLayoutType']) ||
        CellLayoutType ===
          ('Medium' as Fd.MenuSectionItemBase['CellLayoutType']) ||
        CellLayoutType === Api.CellLayoutType.Small ||
        CellLayoutType === Api.CellLayoutType.Medium ? (
          <div className="menu__items__item__img-holder__img__skeleton">
            <Skeleton width={120} height={120} />
          </div>
        ) : (
          <div className="menu__items__item__img-holder__img__skeleton">
            <Skeleton width={'100%'} height={180} />
          </div>
        );

      img = (
        <ProgressiveImage src={url} placeholder="">
          {(src, loading) => {
            return loading ? (
              placeholder
            ) : (
              <LazyLoad
                height={height}
                placeholder={<Skeleton width={'100%'} height={height} />}
              >
                <img
                  src={src}
                  srcSet={`${src} 1x, ${src}&dpr=2 2x, ${src}&dpr=3 3x`}
                  alt=""
                />
              </LazyLoad>
            );
          }}
        </ProgressiveImage>
      );
    }

    let index = -1;
    let item: any = {};

    for (let i = Basket.items.length - 1; i >= 0; i--) {
      if (Basket.items[i].Item.MenuItemId === MenuItemId) {
        index = i;
        item = Basket.items[i];
        break;
      }
    }

    const subtractItem = () => {
      Track('Restaurants', 'Remove Item From Basket Via Menu', {
        event_label: item.Item.Name,
        virtualRestaurantId: this.props.vrid,
        menuId: this.props.MenuId,
      });

      RemoveItemAt({
        menuId: this.props.MenuId,
        index,
        indexName: item.Item.Name,
      });
    };

    const addItem = () => {
      Track('Restaurants', 'Add Item To Basket Via Menu', {
        event_label: item.Item.Name,
        virtualRestaurantId: this.props.vrid,
        name: item.Item.Name,
        itemType: item.Options.length === 0 ? 'simple' : 'complex',
        menuId: this.props.MenuId,
      });
      AddItemToBasket(item);
    };

    const IsAvailable = Item.IsAvailable;

    const isImageNull: boolean = img === null;
    const isImageNullDescriptionEmpty: boolean =
      isImageNull && !Item.Description;

    let controlStyle = styles.control;
    let amountStyle = styles.primary;

    if (isImageNullDescriptionEmpty) {
      controlStyle = styles.control;
      amountStyle = styles.primary;
    } else if (
      isImageNull ||
      CellLayoutType === Api.CellLayoutType.Small ||
      CellLayoutType ===
        ('Small' as Fd.MenuSectionItemBase['CellLayoutType']) ||
      CellLayoutType === Api.CellLayoutType.Medium ||
      CellLayoutType ===
        ('Medium' as Fd.MenuSectionItemBase['CellLayoutType']) ||
      CellLayoutType === Api.CellLayoutType.Large ||
      CellLayoutType === ('Large' as Fd.MenuSectionItemBase['CellLayoutType'])
    ) {
      controlStyle = isDark ? styles.controlThree : styles.controlTwo;
      amountStyle = isDark ? styles.primary : styles.tertiary;
    }

    const controls = (
      <div
        className="flex flex-justify-center flex-align-center  menu__items__item__controls"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          style={{
            background: 'none',
            color: 'inherit',
            border: 'none',
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
          }}
          tabIndex={0}
          onClick={subtractItem}
          aria-label={`${translate('Remove')}` + Name}
        >
          <i
            className="tertiary icon_minus_alt2"
            style={controlStyle}
            key={`minus-${MenuItemId}`}
          />
        </button>
        <p className="tertiary clear-margins fd-h4" style={amountStyle}>
          {basketCount}
        </p>
        <button
          style={{
            background: 'none',
            color: 'inherit',
            border: 'none',
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
          }}
          tabIndex={0}
          onClick={addItem}
          aria-label={`${translate('Add')}` + Name}
        >
          <i
            className="tertiary icon_plus_alt2"
            style={controlStyle}
            key={`plus-${MenuItemId}`}
          />
        </button>
      </div>
    );

    let content: JSX.Element | null = null;

    const currency = ToCurrency(
      Item.ActualPrice,
      this.props.Currency,
      initialLang,
    );

    if (!IsAvailable) {
      return null;
    } else {
      //For web-order mode---------------------------------------------------------
      // Name, no image, no description
      if (isImageNullDescriptionEmpty) {
        content = (
          <div
            className={`menu__items__item menu__items__item--noimg-nodesc flex ${
              basketCount > 0 ? 'menu__items__item--added' : ''
            } menu__items__item--noedit`}
            style={isDark ? styles.darkItem : styles.item}
            key={`item-${MenuItemId}`}
          >
            <div className="flex-one">
              <div
                className="flex flex-align-center flex-justify-between menu__items__item__header"
                style={
                  basketCount > 0
                    ? styles.added
                    : Radium.getState(
                        this.state,
                        `item-${MenuItemId}`,
                        ':active',
                      )
                    ? styles.backgroundSecondary
                    : null
                }
                key={`item-header-${MenuItemId}`}
              >
                {Name ? (
                  <button
                    tabIndex={0}
                    aria-label={
                      Name + (from && `${translate('From')}`) + currency
                    }
                    className="removeButtonSimpleStyles tertiary clear-margins bold ie-flex-one wrap-text fd-h4"
                    style={
                      Radium.getState(
                        this.state,
                        `item-${MenuItemId}`,
                        ':active',
                      ) || basketCount > 0
                        ? styles.primary
                        : Radium.getState(
                            this.state,
                            `item-${MenuItemId}`,
                            ':hover',
                          )
                        ? styles.secondary
                        : isDark
                        ? styles.primary
                        : styles.tertiary
                    }
                  >
                    <Typography variant="h4" component="span">
                      <strong>{Name}</strong>
                    </Typography>
                  </button>
                ) : null}
                <div className="flex flex-justify-end menu__items__item__header__price">
                  {prePrice}
                  <p
                    className="secondary clear-margins bold fd-h3"
                    style={
                      Radium.getState(
                        this.state,
                        `item-${MenuItemId}`,
                        ':active',
                      ) || basketCount > 0
                        ? styles.primary
                        : styles.secondary
                    }
                  >
                    {currency}
                  </p>
                  {postPrice}
                  {controls}
                </div>
              </div>
            </div>
          </div>
        );
      } else if (isImageNull) {
        // name, description, no image
        content = (
          <div
            className={`menu__items__item menu__items__item--noimg flex ${
              basketCount > 0 ? 'menu__items__item--added' : ''
            } menu__items__item--noedit`}
            style={isDark ? styles.darkItem : styles.item}
            key={`item-${MenuItemId}`}
          >
            <div className="flex-one">
              <div
                className="flex flex-align-center flex-justify-between menu__items__item__header"
                style={
                  basketCount > 0
                    ? styles.added
                    : Radium.getState(
                        this.state,
                        `item-${MenuItemId}`,
                        ':active',
                      )
                    ? styles.backgroundSecondary
                    : null
                }
                key={`item-header-${MenuItemId}`}
              >
                {Name ? (
                  <p
                    className="tertiary clear-margins bold ie-flex-one wrap-text fd-h4"
                    style={
                      Radium.getState(
                        this.state,
                        `item-${MenuItemId}`,
                        ':active',
                      ) || basketCount > 0
                        ? styles.primary
                        : Radium.getState(
                            this.state,
                            `item-${MenuItemId}`,
                            ':hover',
                          )
                        ? styles.secondary
                        : isDark
                        ? styles.primary
                        : styles.tertiary
                    }
                  >
                    <Typography variant="h4" component="span">
                      <strong>{Name}</strong>
                    </Typography>
                  </p>
                ) : null}
                <div className="flex flex-justify-end menu__items__item__header__price">
                  {prePrice}
                  <p
                    className="secondary clear-margins bold fd-h3"
                    style={
                      Radium.getState(
                        this.state,
                        `item-${MenuItemId}`,
                        ':active',
                      ) || basketCount > 0
                        ? styles.primary
                        : styles.secondary
                    }
                  >
                    {currency}
                  </p>
                  {postPrice}
                </div>
              </div>
              <div className="flex flex-justify-between menu__items__item__desc">
                {Description ? (
                  <button
                    aria-label={
                      Name +
                      ' , ' +
                      Description +
                      (from && `${translate('From')}`) +
                      currency
                    }
                    className="removeButtonStyles tertiary clear-margins ie-flex-one menu__items__item__desc--desc fd-h4"
                    style={isDark ? styles.primary : styles.tertiary}
                  >
                    <Typography
                      variant="h6"
                      color="textPrimary"
                      component="span"
                    >
                      {descriptionWithLineBreaks}
                    </Typography>
                  </button>
                ) : null}
                {controls}
              </div>
            </div>
          </div>
        );
      } else if (
        CellLayoutType === Api.CellLayoutType.Small ||
        CellLayoutType ===
          ('Small' as Fd.MenuSectionItemBase['CellLayoutType']) ||
        CellLayoutType === Api.CellLayoutType.Medium ||
        CellLayoutType ===
          ('Medium' as Fd.MenuSectionItemBase['CellLayoutType'])
      ) {
        // image (CellLayoutType, small, medium), name, description
        content = (
          <div
            className={`flex menu__items__item menu__items__item--img-small ${
              basketCount > 0 ? 'menu__items__item--added' : ''
            } menu__items__item--noedit`}
            style={isDark ? styles.darkItem : styles.item}
            key={`item-${MenuItemId}`}
          >
            <div className="menu__items__item__img-holder">
              <div
                className="menu__items__item__img-holder__img"
                style={
                  Radium.getState(
                    this.state,
                    `item-${MenuItemId}`,
                    ':active',
                  ) ||
                  Radium.getState(this.state, `item-${MenuItemId}`, ':hover')
                    ? styles.imgHover
                    : basketCount > 0
                    ? styles.borderSecondary
                    : null
                }
              >
                {img}

                <div
                  className="flex flex-justify-center menu__items__item__img-holder__img__details"
                  style={
                    Radium.getState(
                      this.state,
                      `item-${MenuItemId}`,
                      ':active',
                    ) || basketCount > 0
                      ? styles.backgroundSecondaryOpacity
                      : null
                  }
                >
                  {prePrice}
                  <p
                    className="secondary clear-margins bold fd-h3"
                    style={
                      Radium.getState(
                        this.state,
                        `item-${MenuItemId}`,
                        ':active',
                      ) || basketCount > 0
                        ? styles.primary
                        : styles.secondary
                    }
                  >
                    {currency}
                  </p>
                  {postPrice}
                </div>
              </div>
              {controls}
            </div>
            <button
              tabIndex={0}
              aria-label={
                Name +
                ' , ' +
                Description +
                (from && `${translate('From')}`) +
                currency
              }
              className="removeButtonSimpleStyles menu__items__item__desc flex flex-column flex-align-start flex-one"
            >
              <div className="flex fill-width">
                {Name ? (
                  <p
                    className="tertiary clear-margin-top bold ie-flex-one fd-h4"
                    style={
                      Radium.getState(
                        this.state,
                        `item-${MenuItemId}`,
                        ':active',
                      ) ||
                      Radium.getState(
                        this.state,
                        `item-${MenuItemId}`,
                        ':hover',
                      ) ||
                      basketCount > 0
                        ? styles.secondary
                        : isDark
                        ? styles.primary
                        : styles.tertiary
                    }
                  >
                    <Typography variant="h4" component="span">
                      <strong>{Name}</strong>
                    </Typography>
                  </p>
                ) : null}
              </div>
              <div className="flex fill-width">
                {Description ? (
                  <Typography variant="h6" color="textPrimary" component="span">
                    {descriptionWithLineBreaks}
                  </Typography>
                ) : null}
              </div>
            </button>
          </div>
        );
      } else if (
        CellLayoutType === Api.CellLayoutType.Large ||
        CellLayoutType === ('Large' as Fd.MenuSectionItemBase['CellLayoutType'])
      ) {
        content = (
          // image CellLayoutType === large, name and description
          <div
            className={`flex menu__items__item menu__items__item--img-big ${
              basketCount > 0 ? 'menu__items__item--added' : ''
            } menu__items__item--noedit`}
            style={isDark ? styles.darkItem : styles.item}
            key={`item-${MenuItemId}`}
          >
            <div className="flex-one">
              <div className="menu__items__item__img-holder">
                <div
                  className="menu__items__item__img-holder__img"
                  style={
                    Radium.getState(
                      this.state,
                      `item-${MenuItemId}`,
                      ':active',
                    ) ||
                    Radium.getState(this.state, `item-${MenuItemId}`, ':hover')
                      ? styles.imgHover
                      : basketCount > 0
                      ? styles.borderSecondary
                      : null
                  }
                >
                  {img}

                  <div
                    className="flex flex-justify-between flex-align-center menu__items__item__img-holder__img__details"
                    style={
                      Radium.getState(
                        this.state,
                        `item-${MenuItemId}`,
                        ':active',
                      ) || basketCount > 0
                        ? styles.backgroundSecondaryOpacity
                        : null
                    }
                  >
                    {Name ? (
                      <>
                        {!Description ? (
                          <button
                            aria-label={
                              Name +
                              ' , ' +
                              Description +
                              (from && `${translate('From')}`) +
                              currency
                            }
                            className="removeButtonSimpleStyles tertiary clear-margins bold ie-flex-one fd-h4"
                          >
                            <Typography variant="h4" component="span">
                              <strong>{Name}</strong>
                            </Typography>
                          </button>
                        ) : (
                          <p
                            className="tertiary clear-margins bold ie-flex-one fd-h4"
                            style={
                              Radium.getState(
                                this.state,
                                `item-${MenuItemId}`,
                                ':active',
                              ) || basketCount > 0
                                ? styles.primary
                                : Radium.getState(
                                    this.state,
                                    `item-${MenuItemId}`,
                                    ':hover',
                                  )
                                ? styles.secondary
                                : styles.tertiary
                            }
                          >
                            <Typography variant="h4" component="span">
                              <strong>{Name}</strong>
                            </Typography>
                          </p>
                        )}
                      </>
                    ) : null}

                    <div className="flex">
                      {prePrice}
                      <p
                        className="secondary clear-margins bold fd-h3"
                        style={
                          Radium.getState(
                            this.state,
                            `item-${MenuItemId}`,
                            ':active',
                          ) || basketCount > 0
                            ? styles.primary
                            : styles.secondary
                        }
                      >
                        {currency}
                      </p>
                      {postPrice}
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`menu__items__item__desc flex-one ${
                  Description === '' ? 'menu__items__item__desc--none' : ''
                } flex flex-align-start flex-justify-between`}
              >
                {Description ? (
                  <button
                    aria-label={
                      Name +
                      ' , ' +
                      Description +
                      (from && `${translate('From')}`) +
                      currency
                    }
                    className="removeButtonStyles tertiary clear-margins ie-flex-one menu__items__item__desc--desc fd-h4"
                    style={isDark ? styles.primary : styles.tertiary}
                  >
                    <Typography
                      variant="h6"
                      color="textPrimary"
                      component="span"
                    >
                      {descriptionWithLineBreaks}
                    </Typography>
                  </button>
                ) : null}
                {controls}
              </div>
            </div>
          </div>
        );
      }
    }

    return (
      <div
        data-basket-count={this.props.basketCount}
        onClick={this.props.HandleClickItem}
        id={`menu-item-${MenuItemId}`}
      >
        {content}
      </div>
    );
  }
}

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: Store.State) => {
  return {
    AppConfig: state.flipdish.AppConfig,
  };
};

export default connect<MappedState, {}, Props>(
  mapStateToProps,
  null,
)(Radium(FlipdishMenuItem));
